import Vuex, {
  MutationTree,
  GetterTree,
  ActionTree,
  Module,
  ActionContext,
} from 'vuex'
import Vue from 'vue'
import { ISectionFormWithoutThemeAndSectionData } from '@/types'
import { RootState } from '..'
import cms from '../../cms'
import forms from '@/rest/forms'

Vue.use(Vuex)

export interface FormsState {
  newsletter: ISectionFormWithoutThemeAndSectionData | null;
  competition: ISectionFormWithoutThemeAndSectionData | null;
  isNewsletterSubmitted: boolean
  isCompetitionSubmitted: boolean
}

export const formsState: FormsState = {
  newsletter: null,
  competition: null,
  isNewsletterSubmitted: false,
  isCompetitionSubmitted: false,
}

const formsMutations: MutationTree<FormsState> = {
  SET_FORM_NEWSLETTER (state: FormsState, form: ISectionFormWithoutThemeAndSectionData|null) {
    state.newsletter = form
  },
  SET_FORM_COMPETITION (state: FormsState, form: ISectionFormWithoutThemeAndSectionData|null) {
    state.competition = form
  },
  SET_FORM_IS_COMPETITION_SUBMITTED (state: FormsState, isCompetitionSubmitted: boolean) {
    state.isCompetitionSubmitted = isCompetitionSubmitted
  },
  SET_FORM_IS_NEWSLETTER_SUBMITTED (state: FormsState, isNewsletterSubmitted: boolean) {
    state.isNewsletterSubmitted = isNewsletterSubmitted
  },
}

const formsActions: ActionTree<FormsState, RootState> = {
  async SUBMIT_NEWSLETTER_FORM (ctx: ActionContext<FormsState, RootState>, data: { email: string, country: string, name: string, consents: {accepted: boolean, id: number}[], newsletterFields?: Record<string, string> }) {
    if (!cms.newsletter.enabled) {
      // Mark submitted anyway because combined form with cms newsletter disabled causes problems
      ctx.commit('SET_FORM_IS_NEWSLETTER_SUBMITTED', true)
      return
    }
    const email = data.newsletterFields?.pers_email || data.email
    if (!email) {
      console.warn('Newsletter form marked as submitted because email is empty')
      ctx.commit('SET_FORM_IS_NEWSLETTER_SUBMITTED', true)
      return
    }
    await cms.newsletter.signup({
      email: email,
      country: data.country,
      name: data.name,
      consents: data.consents,
      ...data.newsletterFields,
    })
    ctx.commit('SET_FORM_IS_NEWSLETTER_SUBMITTED', true)
  },
  async SUBMIT_COMPETITION_FORM (ctx: ActionContext<FormsState, RootState>, data: { fields: Record<string, string | boolean> }) {
    const locale = cms.i18n.currentLocale.code
    const timed_sub = await cms.signedTimestamp.getTimedSubmission()
    await forms.signup(ctx.rootState.campaignId, { fields: data.fields, locale, timed_submission: timed_sub })
    ctx.commit('SET_FORM_IS_COMPETITION_SUBMITTED', true)
  },
}

const formsGetters: GetterTree<FormsState, RootState> = {
  formNewsletter: (state: FormsState) => state.newsletter,
  formCompetition: (state: FormsState) => state.competition,
  formIsCompetitionSubmitted: (state: FormsState) => state.isCompetitionSubmitted,
  formIsNewsletterSubmitted: (state: FormsState) => state.isNewsletterSubmitted,
  formAllFormsAreSubmitted: (state: FormsState) => {
    const combined = state.newsletter?.data.formType === 'combined' || state.competition?.data.formType === 'combined'
    if (combined) {
      return state.isNewsletterSubmitted && state.isCompetitionSubmitted
    }
    const forms = [[state.newsletter, state.isNewsletterSubmitted], [state.competition, state.isCompetitionSubmitted]]
    for (const [form, submitted] of forms) {
      if (!form) {
        continue
      }
      if (!submitted) {
        return false
      }
    }
    return true
  }
}

export const formsModule: Module<FormsState, RootState> = {
  state: formsState,
  mutations: formsMutations,
  getters: formsGetters,
  actions: formsActions,
}
